import React, { useEffect } from 'react';
import { NextPageWithLayout } from './_app';
import ProfileLayout from 'components/ProfileLayout';
import { H4 } from 'components/Text';
import { useTranslation } from 'next-i18next/pages';
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations';
import useBookingDifferences from 'hooks/useBookingDifferences';
import NoResult from 'components/NoResult';
import Spinner from 'components/Spinner';
import FlightRoute from 'components/FlightRoute';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';

interface INotificationsProps {}

const Notifications: NextPageWithLayout<INotificationsProps> = ({}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const { data, isLoading, fetchData } = useBookingDifferences();
  useEffect(() => {
    const { id } = router.query;
    if (id) {
      if (typeof id !== 'string') {
        router.replace('/');
        return;
      }
      // If order id is present in the url fetch the data for the order id
      fetchData(id);
    } else {
      fetchData();
    }
  }, [fetchData, router]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div role="status" className="flex justify-center py-16">
          <Spinner />
          <span className="sr-only">{t('Loading...')}</span>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <NoResult
          title={t('You have no notifications')}
          description={t('Search among our flights.')}
          showButton
        />
      );
    }
    return (
      <div className="pt-4">
        <div className="md:items-start">
          {data?.map((d, idx) => (
            <div key={idx} className="flex flex-col mt-4 relative">
              <div className="flex mb-2 w-full overflow-x-auto scrollbar absolute top-0 left-0">
                {d.booking.request.persons.map((p, pIdx) => (
                  <H4
                    className={`whitespace-nowrap ${
                      pIdx === 0 ? 'pr-4' : 'px-4'
                    } ${
                      pIdx !== d.booking.request.persons.length - 1 &&
                      d.booking.request.persons.length > 1 &&
                      'border-r-2 border-r-tt-yellow'
                    }`}
                    bolded
                    key={`${p.firstName}-${p.middleName}-${p.lastName}-${p.birthDate}`}>
                    {p.firstName} {p.middleName} {p.lastName}
                  </H4>
                ))}
              </div>
              <div className="md:items-start flex flex-col my-8 md:flex-row space-x-0 space-y-4 md:space-x-4 md:space-y-0">
                <div className="w-full md:w-1/2">
                  <H4>{t('Outbound')}</H4>
                  <FlightRoute
                    showDetails
                    containerClassName="md:items-start flex p-4 w-full flex-col rounded-2xl bg-white cursor-pointer"
                    pnr={d.ticket?.response?.pnr?.firstLeg}
                    orderStatus={d.status}
                    ticket_number={
                      d.ticket?.response?.tickets?.firstLeg &&
                      d.ticket?.response?.tickets?.firstLeg.length > 0 &&
                      d.ticket?.response?.tickets?.firstLeg[0] !== null &&
                      d.ticket?.response?.tickets?.firstLeg[0].ticket_number
                        ? d.ticket?.response?.tickets?.firstLeg[0]
                            .ticket_number[0]
                        : undefined
                    }
                    departure={
                      d.booking.response.flightData.firstLeg?.flightSegments[0]
                        ?.departure
                    }
                    arrival={
                      d.booking.response.flightData.firstLeg?.flightSegments?.[
                        d.booking.response.flightData.firstLeg?.flightSegments
                          .length - 1
                      ]?.arrival
                    }
                    marketinAirlineName={
                      d.booking.response.flightData.firstLeg?.flightSegments[0]
                        .marketingAirlineName
                    }
                    elapsedTime={
                      d.booking.response.flightData.firstLeg?.elapsedTime
                    }
                    flightSegments={
                      d.booking.response.flightData.firstLeg?.flightSegments
                    }
                  />
                </div>

                {d.booking.response.flightData.returnLeg && (
                  <div className="w-full md:w-1/2">
                    <H4>{t('Return')}</H4>
                    <FlightRoute
                      showDetails
                      pnr={d.ticket?.response?.pnr?.returnLeg}
                      orderStatus={d.status}
                      ticket_number={
                        d.ticket?.response?.tickets?.returnLeg &&
                        d.ticket?.response?.tickets?.returnLeg.length > 0 &&
                        d.ticket?.response?.tickets?.returnLeg[0] !== null &&
                        d.ticket?.response?.tickets?.returnLeg[0].ticket_number
                          ? d.ticket?.response?.tickets?.returnLeg[0]
                              .ticket_number[0]
                          : undefined
                      }
                      containerClassName="md:items-start flex p-4 w-full flex-col rounded-2xl bg-white cursor-pointer"
                      departure={
                        d.booking.response.flightData.returnLeg
                          ?.flightSegments[0]?.departure
                      }
                      arrival={
                        d.booking.response.flightData.returnLeg
                          ?.flightSegments[0]?.arrival
                      }
                      marketinAirlineName={
                        d.booking.response.flightData.returnLeg
                          ?.flightSegments[0].marketingAirlineName
                      }
                      elapsedTime={
                        d.booking.response.flightData.returnLeg?.elapsedTime
                      }
                      flightSegments={
                        d.booking.response.flightData.returnLeg?.flightSegments
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return <div className="md:pl-10 flex flex-col flex-1">{renderContent()}</div>;
};

Notifications.getLayout = (page: React.ReactElement) => (
  <ProfileLayout
    titleProduction="meta_title_notifications"
    titleStaging="Notifications"
    metaDescriptionProd="meta_description_production_notifications"
    metaDescriptionStaging="Allmlyles API showcase notifications - you can get latest information about your booked flights such as flight changes"
    canonicalProd="https://tipptour.com/notifications"
    canonicalStaging="https://showcase.allmyles.com/notifications">
    {page}
  </ProfileLayout>
);

export default Notifications;

export const getServerSideProps: GetServerSideProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en')),
  },
});
